/**
 * Asks the backend (LocationAvailabilityService) whether a point is inside the
 * service area. The backend reverse geocodes the point and matches it against
 * the active cities, so the browser never decides validity on its own.
 *
 * @param {string} url
 * @param {{ lat: number, lng: number, address?: string|null }} payload
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ isValid: boolean, address: string|null, city: string|null, province: string|null, availableCities: string[] }>}
 */
export async function checkLocation(url, payload, signal) {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Accept-Language': document.documentElement.lang || 'ar',
        },
        body: JSON.stringify(payload),
        signal,
    });

    if (!response.ok) {
        throw new Error(`Location check failed (${response.status}).`);
    }

    const { data } = await response.json();

    if (data.is_available) {
        return {
            isValid: true,
            address: data.location.address ?? payload.address ?? null,
            city: data.location.city ?? null,
            province: data.location.province ?? null,
            availableCities: [],
        };
    }

    return {
        isValid: false,
        address: data.formatted_address ?? payload.address ?? null,
        city: data.detected_city ?? null,
        province: null,
        availableCities: (data.available_cities ?? []).map((city) => city.name),
    };
}
